import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/TradeAuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Mic, Square, Trash2, Save, Play, FileAudio, Clock, FileText, Copy
} from "lucide-react";
import { toast } from "sonner";

const CABINETS = [
  { value: "shipping", label: "Shipping Documents" },
  { value: "company", label: "Company File Cabinet" },
  { value: "roof", label: "Goldspot Roof Sheeting" },
  { value: "misc", label: "Miscellaneous" },
  { value: "usa", label: "USA File Cabinet" },
];

const CABINET_LABELS = Object.fromEntries(CABINETS.map(c => [c.value, c.label]));

const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const AudioPage = () => {
  const { API, authHeader, token } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);

  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [name, setName] = useState("");
  const [cabinet, setCabinet] = useState("misc");
  const [shipmentId, setShipmentId] = useState("");
  const [saving, setSaving] = useState(false);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const [transcripts, setTranscripts] = useState({}); // docId -> text
  const [transcribing, setTranscribing] = useState(null);
  const [openTranscript, setOpenTranscript] = useState(null);
  const [transcribeLang, setTranscribeLang] = useState("auto");
  const [playingId, setPlayingId] = useState(null);

  const handleTranscribe = async (r) => {
    const existing = transcripts[r.id] || r.transcript;
    if (existing) {
      setTranscripts(prev => ({ ...prev, [r.id]: existing }));
      setOpenTranscript(openTranscript === r.id ? null : r.id);
      return;
    }
    setTranscribing(r.id);
    try {
      const res = await axios.post(`${API}/trade/documents/${r.id}/transcribe?lang=${transcribeLang}`, {}, { headers: authHeader() });
      setTranscripts(prev => ({ ...prev, [r.id]: res.data.transcript }));
      setOpenTranscript(r.id);
      toast.success("Transcribed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transcription failed");
    } finally {
      setTranscribing(null);
    }
  };

  const copyTranscript = async (id) => {
    try {
      await navigator.clipboard.writeText(transcripts[id]);
      toast.success("Text copied");
    } catch {}
  };

  // Stop the mic if the page unmounts mid-recording
  useEffect(() => () => {
    clearInterval(timerRef.current);
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
  }, []);

  const fetchData = () => {
    Promise.all([
      axios.get(`${API}/trade/shipments`, { headers: authHeader() }),
      axios.get(`${API}/trade/documents`, { headers: authHeader() }),
    ]).then(([s, d]) => {
      setShipments(s.data);
      setRecordings(d.data.filter(doc => doc.doc_type === "audio_note"));
    }).catch(e => console.error(e))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        const now = new Date();
        setName(`Voice note ${now.toISOString().slice(0, 10)} ${now.toTimeString().slice(0, 5)}`);
      };
      rec.start();
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
      setRecording(true);
      toast.success("Recording started — speak now");
    } catch {
      toast.error("Microphone access denied — allow the microphone and try again");
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recording) {
      recorderRef.current.stop();
      clearInterval(timerRef.current);
      setRecording(false);
    }
  };

  const discard = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setSeconds(0);
  };

  const saveRecording = async () => {
    if (!audioBlob) return;
    if (cabinet === "shipping" && !shipmentId) {
      toast.error("Pick the shipment for this recording");
      return;
    }
    setSaving(true);
    try {
      const ext = (audioBlob.type.split("/")[1] || "webm").split(";")[0];
      const file = new File([audioBlob], `${name || "Voice note"}.${ext}`, { type: audioBlob.type });
      const formData = new FormData();
      formData.append("file", file);
      formData.append("doc_type", "audio_note");
      formData.append("description", name || "Voice note");
      formData.append("category", cabinet);
      formData.append("shipment_id", cabinet === "shipping" ? shipmentId : "");
      await axios.post(`${API}/trade/documents/upload`, formData, {
        headers: { ...authHeader(), "Content-Type": "multipart/form-data" }
      });
      toast.success(`Saved to ${CABINET_LABELS[cabinet]}`);
      discard();
      fetchData();
    } catch {
      toast.error("Failed to save recording");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this recording?")) return;
    try {
      await axios.delete(`${API}/trade/documents/${id}`, { headers: authHeader() });
      toast.success("Recording deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="trade-section pt-32 lg:pt-20 pb-20 min-h-screen">
      <div className="lg:ml-64 px-6 md:px-12 py-10 max-w-4xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <p className="text-xs uppercase tracking-[0.3em] trade-gold mb-3">Voice Notes</p>
          <h1 className="text-4xl md:text-5xl font-serif font-bold">Audio Recorder</h1>
          <p className="text-white/50 mt-2">Record a voice note, then choose which cabinet to file it in</p>
        </motion.div>

        {/* Recorder */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="trade-card rounded-sm p-6 mb-8"
          data-testid="audio-recorder"
        >
          <div className="flex flex-col items-center py-4">
            {!audioBlob ? (
              <>
                <button
                  onClick={recording ? stopRecording : startRecording}
                  className={`w-24 h-24 rounded-full flex items-center justify-center transition-all ${
                    recording
                      ? "bg-red-500 animate-pulse"
                      : "bg-[#D4AF37] hover:bg-[#c19b2e]"
                  }`}
                  data-testid="record-btn"
                >
                  {recording
                    ? <Square size={32} className="text-white" fill="white" />
                    : <Mic size={36} className="text-black" />
                  }
                </button>
                <p className="mt-4 text-sm text-white/60 flex items-center gap-2" data-testid="record-status">
                  <Clock size={14} className="trade-gold" />
                  {recording ? `Recording... ${fmtTime(seconds)}` : "Tap the mic to start recording"}
                </p>
              </>
            ) : (
              <div className="w-full space-y-4">
                <audio controls src={audioUrl} className="w-full" data-testid="audio-playback" />
                <div>
                  <Label className="text-xs text-white/60">Recording name</Label>
                  <Input value={name} onChange={e => setName(e.target.value)} className="trade-input mt-1 h-11 rounded-sm" data-testid="audio-name-input" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-white/60">Save to cabinet</Label>
                    <Select value={cabinet} onValueChange={setCabinet}>
                      <SelectTrigger className="trade-input mt-1 h-11 rounded-sm" data-testid="audio-cabinet-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#142039] border-white/10">
                        {CABINETS.map(c => (
                          <SelectItem key={c.value} value={c.value} data-testid={`cabinet-${c.value}`}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {cabinet === "shipping" && (
                    <div>
                      <Label className="text-xs text-white/60">Shipment *</Label>
                      <Select value={shipmentId} onValueChange={setShipmentId}>
                        <SelectTrigger className="trade-input mt-1 h-11 rounded-sm" data-testid="audio-shipment-select">
                          <SelectValue placeholder="Choose shipment" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#142039] border-white/10 max-h-72">
                          {shipments.map(s => (
                            <SelectItem key={s.id} value={s.id}>{s.shipment_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <div className="flex gap-3">
                  <Button onClick={saveRecording} disabled={saving} className="flex-1 trade-btn-primary rounded-sm h-12" data-testid="save-audio-btn">
                    <Save size={16} className="mr-2" />
                    {saving ? "Saving..." : `Save to ${CABINET_LABELS[cabinet]}`}
                  </Button>
                  <Button onClick={discard} className="bg-white/10 text-white hover:bg-white/20 rounded-sm h-12" data-testid="discard-audio-btn">
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Past recordings */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="text-sm uppercase tracking-widest trade-gold">Saved Recordings</h3>
          <div className="flex items-center gap-2">
            <Label className="text-[11px] text-white/50 whitespace-nowrap">Speech language</Label>
            <Select value={transcribeLang} onValueChange={setTranscribeLang}>
              <SelectTrigger className="trade-input h-9 rounded-sm w-32 text-xs" data-testid="transcribe-lang-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#142039] border-white/10">
                <SelectItem value="auto">Auto-detect</SelectItem>
                <SelectItem value="am">አማርኛ Amharic</SelectItem>
                <SelectItem value="en">English</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {loading ? (
          <div className="text-center p-10 text-white/40">Loading...</div>
        ) : recordings.length === 0 ? (
          <div className="trade-card rounded-sm p-12 text-center">
            <FileAudio className="w-14 h-14 trade-gold mx-auto mb-4 opacity-40" strokeWidth={1} />
            <h3 className="text-xl font-serif mb-2">No recordings yet</h3>
            <p className="text-white/50">Your saved voice notes will appear here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recordings.map((r, i) => (
              <div key={r.id}>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="trade-card rounded-sm p-4 flex items-center gap-4"
                data-testid={`recording-card-${r.id}`}
              >
                <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                  <FileAudio className="w-5 h-5 trade-gold" strokeWidth={1.5} />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="font-semibold text-sm truncate">{r.description || r.original_name}</h4>
                  <p className="text-[10px] text-white/40">
                    {CABINET_LABELS[r.category] || "Shipping Documents"}
                    {r.shipment_name ? ` · ${r.shipment_name}` : ""}
                    {" · "}{r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                  </p>
                </div>
                <button
                  onClick={() => setPlayingId(playingId === r.id ? null : r.id)}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-white/5 text-white/70 hover:bg-white/10 transition-colors flex-shrink-0"
                  data-testid={`play-recording-${r.id}`}
                >
                  <Play size={12} /> {playingId === r.id ? "Stop" : "Play"}
                </button>
                <button
                  onClick={() => handleTranscribe(r)}
                  disabled={transcribing === r.id}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 transition-colors flex-shrink-0"
                  data-testid={`transcribe-recording-${r.id}`}
                >
                  <FileText size={12} />
                  {transcribing === r.id ? "Writing..." : (transcripts[r.id] || r.transcript) ? "Text" : "Transcribe"}
                </button>
                <button onClick={() => handleDelete(r.id)} className="p-2 text-red-400/60 hover:text-red-400 transition-colors flex-shrink-0" data-testid={`delete-recording-${r.id}`}>
                  <Trash2 size={14} />
                </button>
              </motion.div>
              {playingId === r.id && (
                <div className="trade-card rounded-sm p-3 mt-2" data-testid={`player-${r.id}`}>
                  <audio
                    controls
                    autoPlay
                    src={`${API}/trade/documents/${r.id}/preview?auth=${token}`}
                    className="w-full h-10"
                  />
                </div>
              )}
              {openTranscript === r.id && transcripts[r.id] && (
                <div className="trade-card rounded-sm p-4 mt-2 border-l-2 border-[#D4AF37]" data-testid={`transcript-panel-${r.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] uppercase tracking-widest trade-gold">Written Note</p>
                    <button
                      onClick={() => copyTranscript(r.id)}
                      className="flex items-center gap-1 text-[11px] text-white/50 hover:text-[#D4AF37] transition-colors"
                      data-testid={`copy-transcript-${r.id}`}
                    >
                      <Copy size={11} /> Copy
                    </button>
                  </div>
                  <p className="text-sm text-white/85 whitespace-pre-wrap">{transcripts[r.id]}</p>
                </div>
              )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioPage;
